import Link from "next/link";

import {
  Pagination,
  PaginationItemRenderProps,
  PaginationItemType,
} from "@nextui-org/react";

import ChevronLeftIcon from "@/icons/ui/navigation/chevron-left.svg";
import { cn } from "@/utils/cn";
import { buildPageUrl, buildQueryPageUrl } from "@/utils/url/pagination";

import styles from "./style.module.scss";

interface Props {
  totalPage: number;
  currentPage: number;
  className?: string;
  // SEO-friendly pagination - now required
  pathname: string; // Current pathname for URL building

  searchParams: URLSearchParams; // Current query parameters to preserve
  // Pagination type - defaults to path-based for backward compatibility
  paginationType?: "path" | "query";
}

export const PaginationContainer = ({
  totalPage,
  currentPage,
  className,
  pathname,
  searchParams,
  paginationType = "path",
}: Props) => {
  if (totalPage === 1) {
    return null;
  }
  // Helper function to generate URL for a specific page using centralized utility
  const generatePageUrl = (page: number): string => {
    return paginationType === "query"
      ? buildQueryPageUrl(pathname, searchParams, page)
      : buildPageUrl(pathname, searchParams, page);
  };

  const renderItem = ({
    key,
    value,
  }: PaginationItemRenderProps) => {
    if (value === PaginationItemType.NEXT) {
      const nextPage = currentPage + 1;
      const isDisabled = currentPage >= totalPage;

      return (
        <div
          key={key}
          className={"grow"}
        >
          {!isDisabled ? (
            <Link
              href={generatePageUrl(nextPage)}
              className={cn(styles.btn, "ms-auto")}
              rel="next"
              aria-label={`Go to page ${nextPage}`}
            >
              <ChevronLeftIcon className="rotate-180" />
            </Link>
          ) : (
            <div
              className={cn(
                styles.btn,
                "ms-auto",
                "opacity-30 cursor-not-allowed",
              )}
            >
              <ChevronLeftIcon className="rotate-180" />
            </div>
          )}
        </div>
      );
    }

    if (value === PaginationItemType.PREV) {
      const prevPage = currentPage - 1;
      const isDisabled = currentPage <= 1;

      return (
        <div
          key={key}
          className={"grow"}
        >
          {!isDisabled ? (
            <Link
              href={generatePageUrl(prevPage)}
              className={cn(styles.btn)}
              rel="prev"
              aria-label={`Go to page ${prevPage}`}
            >
              <ChevronLeftIcon />
            </Link>
          ) : (
            <div className={cn(styles.btn, "opacity-30 cursor-not-allowed")}>
              <ChevronLeftIcon />
            </div>
          )}
        </div>
      );
    }

    if (value === PaginationItemType.DOTS) {
      return (
        <button
          key={key}
          className="text-[1.8rem] text-gray-1200 lg:text-[2.1rem] w-auto cursor-default"
        >
          ...
        </button>
      );
    }

    // cursor is the default item
    return (
      <Link
        key={key}
        href={generatePageUrl(value)}
        className={cn(
          "text-[1.8rem] text-gray-1200 lg:text-[2.1rem] hover:font-bold w-auto",
          typeof value === "number" && value === currentPage && "font-bold",
        )}
        aria-label={`Go to page ${value}`}
        aria-current={
          typeof value === "number" && value === currentPage ? "page" : undefined
        }
      >
        {value}
      </Link>
    );
  };

  return (
    <div className={cn(styles.container, className)}>
      <Pagination
        className={"w-full"}
        showControls
        total={totalPage}
        initialPage={1}
        page={currentPage}
        size="lg"
        variant="light"
        disableCursorAnimation
        siblings={0}
        renderItem={renderItem}
        classNames={{
          wrapper: "gap-[2rem] w-full max-w-full",
        }}
      />
    </div>
  );
};
